// "Als App installieren": the browser announces early that the app can be installed (beforeinstallprompt, Chrome/
// Edge/Samsung); the announcement is kept here so the profile can offer a button later. iPhone/iPad have no such
// dialog – there the profile shows the steps via the share menu.
type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: InstallPrompt | null = null;
let installedNow = false;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach(l => l());

window.addEventListener('beforeinstallprompt', e => {
  deferred = e as InstallPrompt; // the browser's own banner may still show – the profile button is an extra way
  changed();
});
window.addEventListener('appinstalled', () => {
  deferred = null;
  installedNow = true;
  changed();
});

export const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export type InstallOption = 'installed' | 'prompt' | 'ios' | 'android-menu' | 'none';
/** What the profile can offer on this device */
export function installOption(): InstallOption {
  if (installedNow || isStandalone()) return 'installed';
  if (deferred) return 'prompt';
  if (isIos()) return 'ios';
  if (/Android/i.test(navigator.userAgent)) return 'android-menu'; // e.g. Firefox: "Installieren" in the menu
  return 'none'; // desktop browsers without installation (e.g. Firefox, Safari on the Mac): nothing to offer
}

/** Opens the browser's install dialog; true when the member installed */
export async function promptInstall() {
  if (!deferred) return false;
  const p = deferred;
  deferred = null;
  try {
    await p.prompt();
    const { outcome } = await p.userChoice;
    return outcome === 'accepted';
  } catch {
    return false; // the browser refused the dialog (e.g. already shown once)
  } finally {
    changed();
  }
}

export function onInstallChange(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
